<!-- Help tooltip card, same structure and classes as the ones on the sites list, so the
     two read identically. Content comes in through [tooltipContext]. -->
<ng-template #helpTip let-tip>
  <div class="sw-help-card">
    <div class="sw-help-card-head">
      <span class="sw-help-card-icon" aria-hidden="true">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
          stroke-linecap="round" stroke-linejoin="round">
          <rect width="20" height="16" x="2" y="4" rx="2"></rect>
          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
        </svg>
      </span>
      <span class="sw-help-card-title">{{ tip.title }}</span>
    </div>
    <p class="sw-help-card-body">{{ tip.body }}</p>
    @if (tip.points?.length) {
    <ul class="sw-help-card-points">
      @if (tip.pointsTitle) {
      <li class="sw-help-card-points-title">{{ tip.pointsTitle }}</li>
      }
      @for (point of tip.points; track point) {
      <li>{{ point }}</li>
      }
    </ul>
    }
  </div>
</ng-template>

<div class="sw-site-panel">
  <!-- HEADER — omitted when embedded; the host <sw-drawer> provides the chrome. -->
  @if (!embedded) {
  <div class="sw-site-header">
    <h2 class="sw-site-title">{{siteData ? 'Update Site' : 'Add Site'}}</h2>
    <button type="button" class="sw-site-close" aria-label="Close" (click)="closeModal()">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    </button>
  </div>
  }

  <div class="sw-site-body custom-sw-scroll">
    <form [formGroup]="addSiteForm">

      @if (isEditMode) {
      <!-- ===== EDIT: the same 3-column grid the Add form uses ===== -->
      <div class="sw-add-grid">
        <div class="sw-modal-field"
          [class.invalid-input]="addSiteForm.controls['site_name'].dirty && addSiteForm.controls['site_name'].invalid">
          <label class="form-label">Site Name <span class="sw-req">*</span></label>
          <input type="text" class="form-control" placeholder="Site Name" formControlName="site_name" />
          @if (addSiteForm.controls['site_name'].dirty && addSiteForm.controls['site_name'].errors?.['required']) {
          <div class="text-danger fs-14">Site name is required</div>
          }
        </div>

        <div class="sw-modal-field"
          [class.invalid-input]="addSiteForm.controls['display_name'].dirty && addSiteForm.controls['display_name'].invalid">
          <label class="form-label">Site Display Name <span class="sw-req">*</span></label>
          <input type="text" class="form-control" placeholder="Site Display Name" formControlName="display_name" />
          @if (addSiteForm.controls['display_name'].dirty && addSiteForm.controls['display_name'].errors?.['required']) {
          <div class="text-danger fs-14">Site Display name is required</div>
          }
        </div>

        <div class="sw-modal-field"
          [class.invalid-input]="addSiteForm.controls['CRM_url'].dirty && addSiteForm.controls['CRM_url'].invalid">
          <label class="form-label">CRM URL <span class="sw-req">*</span></label>
          <input type="text" class="form-control" placeholder="CRM Url" formControlName="CRM_url" />
          @if (addSiteForm.controls['CRM_url'].dirty && addSiteForm.controls['CRM_url'].errors?.['required']) {
          <div class="text-danger fs-14">CRM URL is required</div>
          }
        </div>

        <div class="sw-modal-field" [class.sw-modal-field--wide]="pbxPanelOpen">
          <label class="form-label">Speaker</label>
          <div>
            @for (speakerCtrl of speakerNumberControls; track speakerCtrl) {
            <div class="sw-speaker-row">
              <input type="tel" class="form-control" placeholder="Speaker call number" [formControl]="speakerCtrl" />
              <button type="button" class="sw-speaker-remove-btn" (click)="removeSpeakerNumber($index)"
                aria-label="Remove speaker">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            }
          </div>
          <button type="button" class="sw-speaker-add-btn" (click)="addSpeakerNumber()">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add Speaker
          </button>
          <app-pbx-speaker-suggest [siteName]="addSiteForm.get('site_name')?.value" [selected]="speakerNumbers.value" [siteId]="pbxSuggestSiteId"
            (add)="addPbxSpeaker($event)" (cameraChange)="rememberPbxCamera($event)" (openChange)="pbxPanelOpen = $event"></app-pbx-speaker-suggest>
        </div>

        <div class="sw-modal-field">
          <label class="form-label">911 Number to Call</label>
          <input type="tel" class="form-control" placeholder="911 number to call"
            formControlName="emergency_call_number" />
        </div>

        <div class="sw-modal-field">
          <label class="form-label">Client Contact Number</label>
          <input type="tel" class="form-control" placeholder="Client contact number"
            formControlName="client_contact_number" />
        </div>

        <div class="sw-modal-field">
          <label class="form-label">Site Type</label>
          <sw-select [options]="siteTypeOptions" optionLabel="label" optionValue="value" [showClear]="true" [filter]="false"
            placeholder="Select site type" formControlName="site_type" appendTo="body">
          </sw-select>
        </div>


        <div class="sw-modal-field">
          <label class="form-label">Layout Name</label>
          <sw-select [options]="dwLayouts" optionLabel="label" [showClear]="true" [loading]="isDwLayoutsLoading"
            placeholder="Select existing layout" formControlName="existing_layout" appendTo="body">
          </sw-select>
        </div>

        <div class="sw-modal-field">
          <label class="form-label">DW User Assign For Devices</label>
          <sw-select [multiple]="true" [options]="dwUsers" optionLabel="label" [showClear]="true"
            [loading]="isDwUsersLoading" placeholder="Select DW user" formControlName="DW_user" appendTo="body">
          </sw-select>
        </div>

        <!-- One row: the two pickers are read as a pair, and stacking them pushed everything
             below out of view in the drawer.
             Both use appendTo="body": the drawer body scrolls (overflow-y: auto), so an inline
             option panel is clipped at the drawer's edge — which is what hid most of the
             manager list. Appended, PrimeNG positions the panel against the viewport and flips
             it above the trigger when there is no room below. -->
        <!-- Not in .sw-assign-row here: that row caps each field at 22rem so two of them sit
             side by side, and this is the only field in it on the edit form — the cap just made
             it narrower than everything above it. -->
        <div class="sw-modal-field">
          <label class="form-label">Assign Team
            <i class="fa fa-info-circle ms-1 sw-field-info" placement="top" container="body" [ngbTooltip]="helpTip"
              [tooltipContext]="{ $implicit: teamHelpTip }" tooltipClass="sw-help-tip"
              aria-label="About assigning teams"></i>
          </label>
          <sw-select [multiple]="true" [options]="allTeams" optionLabel="team_name" optionValue="_id" [showClear]="true"
            [loading]="isTeamsLoading" placeholder="Select team(s)" formControlName="assigned_teams"
            filterBy="team_name" appendTo="body">
          </sw-select>
        </div>

        <!-- LIVE URLS — the same list Site Settings keeps, on the form that edits everything
             else about the site. Same rows, same endpoint, same rtsp:// rule. -->
        <div class="sw-live-urls">
          <div class="sw-live-urls-head">
            <div>
              <div class="sw-live-urls-title">Live URLs</div>
              <p class="sw-live-urls-note">RTSP streams for this site. Anything listed here is playable from the
                dashboard's live camera view.</p>
            </div>
            <button type="button" class="sw-live-url-add" [disabled]="isLoading" (click)="addLiveUrlRow()">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add URL
            </button>
          </div>

          @for (row of liveUrlRows; track $index) {
          <div class="sw-live-url-row">
            <div class="sw-live-url-grid">
              <div class="sw-modal-field sw-live-url-wide">
                <div class="sw-field-head">
                  <label class="sw-modal-label">RTSP URL</label>
                  <button type="button" class="sw-live-url-remove" [disabled]="isLoading"
                    (click)="removeLiveUrlRow($index)">Remove</button>
                </div>
                <input type="text" class="sw-modal-input" placeholder="rtsp://host:554/stream" [(ngModel)]="row.url"
                  [ngModelOptions]="{ standalone: true }" [disabled]="isLoading">
              </div>
              <div class="sw-modal-field">
                <label class="sw-modal-label">Camera Name</label>
                <input type="text" class="sw-modal-input" placeholder="Camera name" [(ngModel)]="row.name"
                  [ngModelOptions]="{ standalone: true }" [disabled]="isLoading">
              </div>
              <div class="sw-modal-field">
                <label class="sw-modal-label">Username</label>
                <input type="text" class="sw-modal-input" placeholder="Username" [(ngModel)]="row.username"
                  [ngModelOptions]="{ standalone: true }" [disabled]="isLoading" autocomplete="off">
              </div>
              <div class="sw-modal-field">
                <label class="sw-modal-label">Password</label>
                <div class="sw-live-pwd-wrap">
                  <input [type]="row.reveal ? 'text' : 'password'" class="sw-modal-input" placeholder="Password"
                    [(ngModel)]="row.password" [ngModelOptions]="{ standalone: true }" [disabled]="isLoading"
                    autocomplete="new-password">
                  <button type="button" class="sw-live-pwd-toggle" (click)="toggleLiveUrlReveal($index)"
                    [attr.aria-label]="row.reveal ? 'Hide password' : 'Show password'">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      @if (row.reveal) {
                      <path
                        d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24">
                      </path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                      } @else {
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                      }
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
          } @empty {
          <p class="sw-live-urls-note">No live urls yet. Add one to make this site playable in live view.</p>
          }
        </div>

        <ng-container [ngTemplateOutlet]="responseOrderSection"></ng-container>
      </div>
      }

      @else {
      <!-- ===== ADD: 3-column grid ===== -->
      <div class="sw-add-grid">
        <div class="sw-modal-field"
          [class.invalid-input]="addSiteForm.controls['site_name'].dirty && addSiteForm.controls['site_name'].invalid">
          <label class="form-label">Site Name <span class="sw-req">*</span></label>
          <input type="text" class="form-control" placeholder="Site Name" formControlName="site_name" />
          @if (addSiteForm.controls['site_name'].dirty && addSiteForm.controls['site_name'].errors?.['required']) {
          <div class="text-danger fs-14">Site name is required</div>
          }
        </div>

        <div class="sw-modal-field"
          [class.invalid-input]="addSiteForm.controls['display_name'].dirty && addSiteForm.controls['display_name'].invalid">
          <label class="form-label">Site Display Name <span class="sw-req">*</span></label>
          <input type="text" class="form-control" placeholder="Site Display Name" formControlName="display_name" />
          @if (addSiteForm.controls['display_name'].dirty && addSiteForm.controls['display_name'].errors?.['required'])
          {
          <div class="text-danger fs-14">Site Display name is required</div>
          }
        </div>

        <div class="sw-modal-field"
          [class.invalid-input]="addSiteForm.controls['CRM_url'].dirty && addSiteForm.controls['CRM_url'].invalid">
          <label class="form-label">CRM URL <span class="sw-req">*</span></label>
          <input type="text" class="form-control" placeholder="CRM Url" formControlName="CRM_url" />
          @if (addSiteForm.controls['CRM_url'].dirty && addSiteForm.controls['CRM_url'].errors?.['required']) {
          <div class="text-danger fs-14">CRM URL is required</div>
          }
        </div>

        <div class="sw-modal-field" [class.sw-modal-field--wide]="pbxPanelOpen">
          <label class="form-label">Speaker</label>
          <div>
            @for (speakerCtrl of speakerNumberControls; track speakerCtrl) {
            <div class="sw-speaker-row">
              <input type="tel" class="form-control" placeholder="Speaker call number" [formControl]="speakerCtrl" />
              <button type="button" class="sw-speaker-remove-btn" (click)="removeSpeakerNumber($index)"
                aria-label="Remove speaker">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            }
          </div>
          <button type="button" class="sw-speaker-add-btn" (click)="addSpeakerNumber()">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add Speaker
          </button>
          <app-pbx-speaker-suggest [siteName]="addSiteForm.get('site_name')?.value" [selected]="speakerNumbers.value" [siteId]="pbxSuggestSiteId"
            (add)="addPbxSpeaker($event)" (cameraChange)="rememberPbxCamera($event)" (openChange)="pbxPanelOpen = $event"></app-pbx-speaker-suggest>
        </div>

        <div class="sw-modal-field">
          <label class="form-label">911 Number to Call</label>
          <input type="tel" class="form-control" placeholder="911 number to call"
            formControlName="emergency_call_number" />
        </div>

        <div class="sw-modal-field">
          <label class="form-label">Client Contact Number</label>
          <input type="tel" class="form-control" placeholder="Client contact number"
            formControlName="client_contact_number" />
        </div>

        <div class="sw-modal-field">
          <label class="form-label">Site Type</label>
          <sw-select [options]="siteTypeOptions" optionLabel="label" optionValue="value" [showClear]="true" [filter]="false"
            placeholder="Select site type" formControlName="site_type" appendTo="body">
          </sw-select>
        </div>


        <!-- Site option toggles (set at add time; sent in the create payload) -->
        <div class="sw-site-options">
          <label class="form-label">Site Options</label>
          <div class="sw-site-options-grid">

            <div class="form-check form-switch sw-site-option">
              <input class="form-check-input" type="checkbox" role="switch" id="optQuickDisarm"
                formControlName="is_schedule_active">
              <label class="form-check-label" for="optQuickDisarm">Quick disarm</label>
            </div>
            <!-- <div class="form-check form-switch sw-site-option">
              <input class="form-check-input" type="checkbox" role="switch" id="optAlertActive"
                formControlName="is_alert_active">
              <label class="form-check-label" for="optAlertActive">Alert Active</label>
            </div>
            <div class="form-check form-switch sw-site-option">
              <input class="form-check-input" type="checkbox" role="switch" id="optArchiveAlert"
                formControlName="is_archive_alert_active">
              <label class="form-check-label" for="optArchiveAlert">Archive Alert Active</label>
            </div> -->
            <div class="form-check form-switch sw-site-option">
              <input class="form-check-input" type="checkbox" role="switch" id="optFaceDetection"
                formControlName="is_face_detection">
              <label class="form-check-label" for="optFaceDetection">Face detection</label>
            </div>
            <div class="form-check form-switch sw-site-option">
              <input class="form-check-input" type="checkbox" role="switch" id="optVehicleDetection"
                formControlName="is_vehicle_detection">
              <label class="form-check-label" for="optVehicleDetection">Vehicle detection</label>
            </div>
            <div class="form-check form-switch sw-site-option">
              <input class="form-check-input" type="checkbox" role="switch" id="optZoneActive"
                formControlName="is_zone_detection">
              <label class="form-check-label" for="optZoneActive">Zone Active</label>
            </div>
            <!-- Site-wide master switch: with it off, no camera of this site is recorded.
                 Per-camera overrides live in the Cameras drawer. -->
            <div class="form-check form-switch sw-site-option">
              <input class="form-check-input" type="checkbox" role="switch" id="optRtspRecording"
                formControlName="is_rtsp_recording">
              <label class="form-check-label" for="optRtspRecording">RTSP Recording</label>
            </div>
            <div class="form-check form-switch sw-site-option">
              <input class="form-check-input" type="checkbox" role="switch" id="optSendEmail"
                formControlName="is_alert_email_send_active">
              <label class="form-check-label" for="optSendEmail">Send Email Active</label>
            </div>
          </div>
          @if (addSiteForm.get('is_alert_email_send_active')?.value) {
          <div class="sw-modal-field sw-site-option-email">
            <label class="form-label">Add Email</label>
            <input type="email" class="form-control" placeholder="Receive alerts on this email"
              formControlName="receive_alert_send_email" />
          </div>
          }

        </div>

        <ng-container [ngTemplateOutlet]="responseOrderSection"></ng-container>

        <!-- Kept on a full-width row of its own rather than dropped into a grid cell: this
             is the field that decides who hears about a critical incident here. -->
        <div class="sw-assign-team-field sw-assign-row">
          <div class="sw-modal-field">
            <label class="form-label">Assign Team
              <i class="fa fa-info-circle ms-1 sw-field-info" placement="top" container="body" [ngbTooltip]="helpTip"
                [tooltipContext]="{ $implicit: teamHelpTip }" tooltipClass="sw-help-tip"
                aria-label="About assigning teams"></i>
            </label>
            <sw-select [multiple]="true" [options]="allTeams" optionLabel="team_name" optionValue="_id"
              [showClear]="true" [loading]="isTeamsLoading" placeholder="Select team(s)"
              formControlName="assigned_teams" filterBy="team_name" appendTo="body">
            </sw-select>
          </div>

          <!-- Beside Assign Team so it reads as another site-level choice rather than a
               full-width band. Opens the same drawer Site Settings uses; the windows are
               written once Submit has created the site to write them against. -->
          <div class="sw-modal-field">
            <label class="form-label">Authorised Visitor Alert Schedule</label>
            <button type="button" class="sw-visitor-schedule-row" (click)="openVisitorScheduleDrawer()">
              <span class="sw-visitor-schedule-label">
                @if (visitorScheduleCount) {
                {{ visitorScheduleCount }} note{{ visitorScheduleCount > 1 ? 's' : '' }}
                } @else {
                Add visitor windows
                }
              </span>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="sw-visitor-schedule-chevron">
                <path d="m9 18 6-6-6-6"></path>
              </svg>
            </button>
          </div>

          <!-- Alert Schedule. On a new site the editor runs in draft mode: it holds the
               rows here and they are sent once Submit has created the site. -->
          <div class="sw-modal-field">
            <label class="form-label"
              [class.text-danger]="!isEditMode && alertScheduleSubmitted && !alertScheduleCount">Alert Schedule
              @if (!isEditMode) {
              <span class="sw-req">*</span>
              }
            </label>
            <button type="button" class="sw-visitor-schedule-row"
              [class.invalid-input]="!isEditMode && alertScheduleSubmitted && !alertScheduleCount"
              (click)="requestAlertSchedule()">
              <span class="sw-visitor-schedule-label">
                @if (alertScheduleCount) {
                {{ alertScheduleCount }} schedule{{ alertScheduleCount > 1 ? 's' : '' }}
                } @else {
                Add schedule
                }
              </span>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="sw-visitor-schedule-chevron">
                <path d="m9 18 6-6-6-6"></path>
              </svg>
            </button>
            @if (!isEditMode && alertScheduleSubmitted && !alertScheduleCount) {
            <div class="text-danger fs-14">Alert Schedule is required</div>
            }
          </div>
        </div>

        <div class="sw-modal-field">
          <label class="form-label">Layout</label>
          <div class="sw-radio-row">
            <label class="sw-radio-option">
              <input type="radio" formControlName="layout_option" value="new" />
              New Layout
            </label>
            <label class="sw-radio-option">
              <input type="radio" formControlName="layout_option" value="existing" />
              Existing Layout
            </label>
          </div>
        </div>

        @if (isNewLayoutOption) {
        <div class="sw-modal-field">
          <label class="form-label">Server</label>
          <sw-select [options]="dwServers" optionLabel="label" [showClear]="true" [loading]="isDwServersLoading"
            placeholder="Select server" formControlName="DW_server" appendTo="body">
          </sw-select>
        </div>
        <div class="sw-modal-field">
          <label class="form-label">Layout Name</label>
          <input type="text" class="form-control" placeholder="Layout Name" formControlName="layout_name" />
        </div>
        } @else {
        <div class="sw-modal-field">
          <label class="form-label">Layout Name</label>
          <sw-select [options]="dwLayouts" optionLabel="label" [showClear]="true" [loading]="isDwLayoutsLoading"
            placeholder="Select existing layout" formControlName="existing_layout" appendTo="body">
          </sw-select>
        </div>
        }

        <div class="sw-modal-field">
          <label class="form-label">DW User Assign For Devices</label>
          <sw-select [multiple]="true" [options]="dwUsers" optionLabel="label" [showClear]="true"
            [loading]="isDwUsersLoading" placeholder="Select DW user" formControlName="DW_user" appendTo="body">
          </sw-select>
        </div>

        @if (isNewLayoutOption) {
        <div class="sw-modal-field">
          <label class="form-label">Device Events</label>
          <sw-select [options]="dwEvents" optionLabel="label" optionValue="value" [showClear]="true" [filter]="false"
            [loading]="isDwEventsLoading" placeholder="Select events" formControlName="DW_events" appendTo="body">
          </sw-select>
        </div>
        <div class="sw-modal-field">
          <label class="form-label">Device Actions</label>
          <sw-select [options]="dwActions" optionLabel="label" optionValue="value" [showClear]="true" [filter]="false"
            [loading]="isDwActionsLoading" placeholder="Select actions" formControlName="DW_actions" appendTo="body">
          </sw-select>
        </div>
        }
      </div>

      @if (isNewLayoutOption) {
      <div class="sw-device-section">
        <div class="sw-section-header-row">
          <label class="form-label mb-0">Device Create And In Layout</label>
          <button type="button" class="sw-add-device-btn" (click)="addDevice()">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add Device
          </button>
        </div>

        <div formArrayName="DW_devices">
          @for (deviceCtrl of devices.controls; track $index) {
          <div class="sw-device-row" [formGroupName]="$index">
            <div class="sw-device-grid">
              <div class="sw-modal-field"
                [class.invalid-input]="deviceCtrl.get('name')?.dirty && deviceCtrl.get('name')?.invalid">
                <label class="form-label">Device Name</label>
                <input type="text" class="form-control" placeholder="Device name" formControlName="name" />
              </div>
              <div class="sw-modal-field">
                <label class="form-label">Device Type</label>
                <sw-select [options]="deviceTypes" optionLabel="label" optionValue="value" [showClear]="true" [filter]="false"
                  [loading]="isDeviceTypesLoading" placeholder="Select device type" formControlName="deviceType" appendTo="body">
                </sw-select>
              </div>
              <div class="sw-modal-field"
                [class.invalid-input]="deviceCtrl.get('DW_ip_address')?.dirty && deviceCtrl.get('DW_ip_address')?.invalid">
                <label class="form-label">IP Address / RTSP URL</label>
                <input type="text" class="form-control" placeholder="IP Address" formControlName="DW_ip_address" />
              </div>
              <div class="sw-modal-field"
                [class.invalid-input]="deviceCtrl.get('DW_username')?.dirty && deviceCtrl.get('DW_username')?.invalid">
                <label class="form-label">Username</label>
                <input type="text" class="form-control" placeholder="Username" formControlName="DW_username"
                  autocomplete="off" />
              </div>
              <div class="sw-modal-field"
                [class.invalid-input]="deviceCtrl.get('DW_password')?.dirty && deviceCtrl.get('DW_password')?.invalid">
                <label class="form-label">Password</label>
                <input type="password" class="form-control" placeholder="Password" formControlName="DW_password"
                  autocomplete="new-password" />
              </div>
              <div class="sw-modal-field"
                [class.invalid-input]="deviceCtrl.get('DW_description')?.dirty && deviceCtrl.get('DW_description')?.invalid">
                <label class="form-label">Description</label>
                <input type="text" class="form-control" placeholder="Description" formControlName="DW_description" />
              </div>
              <div class="sw-modal-field"
                [class.invalid-input]="deviceCtrl.get('DW_source')?.dirty && deviceCtrl.get('DW_source')?.invalid">
                <label class="form-label">Source</label>
                <input type="text" class="form-control" placeholder="Source" formControlName="DW_source" />
              </div>
              <div class="sw-modal-field sw-device-remove-cell">
                <button type="button" class="sw-remove-device-btn" (click)="removeDevice($index)"
                  [disabled]="devices.length <= 1" aria-label="Remove device">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2">
                    </path>
                  </svg>
                  Remove Device
                </button>
              </div>
            </div>
          </div>
          }
        </div>
      </div>
      }
      }

    </form>
  </div>

  <div class="sw-site-footer">
    <button type="button" class="sw-modal-btn sw-modal-btn--discard" (click)="closeModal()">Discard</button>
    <button type="button" class="sw-modal-btn sw-modal-btn--update" (click)="addSite()" [disabled]="isLoading">
      @if (isLoading) {
      <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
      }
      {{ isLoading ? 'Saving...' : (isEditMode ? 'Save' : 'Submit') }}
    </button>
  </div>
</div>

<!-- The Authorised Visitor Alert Schedule drawer, the same one Site Settings opens. It sits
     over the add form the way the settings drawer sits over the sites list, and its rows are
     held here until Submit creates the site and gives them somewhere to be written. -->
<!-- appendTo="body": this form is itself rendered inside app.component's drawer, so a drawer
     opened from here would sit inside that one's stacking context and end up behind its mask —
     visible, but with nothing on it clickable. Appended to the body it stacks above instead. -->
<sw-drawer [(visible)]="visitorScheduleDrawerOpen" title="Authorised Visitor Alert Schedule"
  width="min(46rem, 94vw)" [showBack]="true" appendTo="body">
  <!-- `drawer-body` is what sw-drawer projects into; without it the panel renders empty. -->
  <div drawer-body class="sw-ts-body">
    <div class="sw-ts-head">
      <p class="sw-ts-head-note">Each note has its own window and covers the whole site.</p>
      <div class="sw-ts-head-actions">
        <!-- Nothing entered yet means nothing to clear, so the button stays out of the way —
             the same rule the sites list uses. -->
        @if (!visitorScheduleEmpty) {
        <button type="button" class="sw-ts-clear" (click)="clearVisitorScheduleRows()">
          Clear All
        </button>
        }
        <button type="button" class="sw-ts-add" (click)="addVisitorScheduleRow()">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Add Note
        </button>
      </div>
    </div>

    @for (row of visitorScheduleRows; track $index) {
    <div class="sw-ts-card">
      <!-- The sites list has an "Applies to" camera picker here. At add time the site has no
           cameras yet — they are created by their first alert — so every window covers the
           whole site, which is what an empty camera list means there too. -->
      <div class="sw-ts-field">
        <div class="sw-field-head">
          <label class="sw-modal-label">Applies to</label>
          @if (visitorScheduleRows.length > 1 || !visitorScheduleRowBlank(row)) {
          <button type="button" class="sw-ts-remove" (click)="removeVisitorScheduleRow($index)">Remove</button>
          }
        </div>
        <!-- The sites list offers each camera here. The site has none yet — a camera is
             created by its first alert — so there is nothing to choose between and this
             states what the window covers instead of offering a list of one. -->
        <p class="sw-ts-applies-note">This note applies to all cameras of the site.</p>
      </div>

      <div class="sw-ts-field sw-ts-times">
        <div>
          <label class="sw-modal-label">Start Date</label>
          <p-datepicker [(ngModel)]="row.start" [showTime]="true" hourFormat="12" [showIcon]="true"
            iconDisplay="input" placeholder="mm/dd/yyyy --:-- --" appendTo="body"
            [defaultDate]="visitorScheduleNow" [minDate]="visitorScheduleToday"
            (ngModelChange)="onVisitorScheduleStartChange(row)" styleClass="sw-time-datepicker"
            panelStyleClass="sw-time-datepicker-panel" [style]="{ width: '100%' }"></p-datepicker>
          @if (visitorScheduleStartError(row)) {
          <p class="sw-ts-error">{{ visitorScheduleStartError(row) }}</p>
          }
        </div>
        <div>
          <label class="sw-modal-label">End Date</label>
          <p-datepicker [(ngModel)]="row.end" [showTime]="true" hourFormat="12" [showIcon]="true"
            iconDisplay="input" placeholder="mm/dd/yyyy --:-- --" appendTo="body"
            [minDate]="visitorScheduleEndMinDate(row)" [defaultDate]="visitorScheduleEndDefaultDate(row)"
            styleClass="sw-time-datepicker" panelStyleClass="sw-time-datepicker-panel"
            [style]="{ width: '100%' }"></p-datepicker>
          @if (visitorScheduleEndError(row)) {
          <p class="sw-ts-error">{{ visitorScheduleEndError(row) }}</p>
          }
        </div>
      </div>

      @if (visitorScheduleWindowLabel(row)) {
      <div class="sw-ts-strip">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
          stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
        <span><strong>Covers</strong> {{ visitorScheduleWindowLabel(row) }}</span>
      </div>
      }

      <div class="sw-ts-field">
        <label class="sw-modal-label">Note</label>
        <textarea class="sw-modal-input sw-temp-schedule-note" rows="3"
          placeholder="Contractors on the roof, delivery window, alarm test…" [(ngModel)]="row.note"></textarea>
        @if (visitorScheduleNoteError(row)) {
        <p class="sw-ts-error">{{ visitorScheduleNoteError(row) }}</p>
        } @else {
        <p class="sw-ts-hint">Shown on the detail page of every alert raised in this window.</p>
        }
      </div>
    </div>
    }
  </div>

  <!-- Save keeps the windows and closes; nothing is written until Submit creates the site and
       gives the endpoint a site id to write against. Discard puts the rows back to what they
       were when the drawer opened. -->
  <div drawer-actions class="sw-side-panel-actions">
    <button type="button" class="sw-modal-btn sw-modal-btn--discard"
      (click)="discardVisitorScheduleRows()">Discard</button>
    <button type="button" class="sw-modal-btn sw-modal-btn--update"
      (click)="saveVisitorScheduleRows()">Save</button>
  </div>
</sw-drawer>

<!-- ALERT SCHEDULE DRAWER — the same editor Site Settings opens, handed the id of the site
     that was just created. Nothing about the schedule feature changes; it simply gets its
     site id later than it does on the sites list. -->
<sw-drawer [(visible)]="alertScheduleDrawerOpen" title="List of schedule" width="80rem" [showBack]="true"
  appendTo="body" (hide)="closeAlertScheduleDrawer()">
  <div drawer-body>
    @if (alertScheduleDrawerOpen) {
    <app-list-of-schedule [embedded]="true" [embSiteId]="schedSiteId" [embUserId]="schedUserId"
      [embScheduleId]="schedId" [embDateDaysActive]="schedDateDaysActive"
      [draftMode]="!isEditMode" [draftSchedules]="alertScheduleDrafts"
      [showScheduleTools]="!isEditMode"></app-list-of-schedule>
    }
  </div>

  <!-- Only while adding. On an existing site the editor writes each row as it is made, so
       there is nothing to keep or throw away here. -->
  @if (!isEditMode) {
  <div drawer-actions class="sw-side-panel-actions">
    <button type="button" class="sw-modal-btn sw-modal-btn--discard"
      (click)="discardAlertScheduleRows()">Discard</button>
    <button type="button" class="sw-modal-btn sw-modal-btn--update"
      (click)="saveAlertScheduleRows()">Save</button>
  </div>
  }
</sw-drawer>

<!-- Incident response order: the numbered steps in the incident popup for this site. Same
     section shell as Live URLs; shared by the add and edit layouts. -->
<ng-template #responseOrderSection>
  <div class="sw-live-urls">
    <div class="sw-live-urls-head">
      <div class="sw-response-head-text">
        <!-- The switch sits beside the title, styled like the Site Options switches. -->
        <div class="sw-response-title-row">
          <div class="sw-live-urls-title">Incident Response Order</div>
          <div class="form-check form-switch sw-site-option">
            <input class="form-check-input" type="checkbox" role="switch" id="optResponseDefault"
              [ngModel]="responseUseDefault" (ngModelChange)="onResponseUseDefaultChange($event)"
              [ngModelOptions]="{ standalone: true }">
            <label class="form-check-label" for="optResponseDefault">Use default order</label>
          </div>
        </div>
        <p class="sw-live-urls-note">The numbered steps operators follow in the incident popup for this site.</p>
      </div>
    </div>
    @if (responseUseDefault) {
    <app-response-order-editor [steps]="defaultResponseSteps" [readonly]="true"></app-response-order-editor>
    <p class="sw-live-urls-note">Set in Settings → Incident Response Order. Changes there apply here too.</p>
    } @else {
    <app-response-order-editor [steps]="responseSteps" (stepsChange)="onSiteResponseStepsChange($event)"></app-response-order-editor>
    }
  </div>
</ng-template>
